import * as Yup from 'yup';
import { emailRegEx, phoneRegEx, websiteRegex, zipCodeRegEx } from '../../../utils/RegularExpressions';

const sponsorSchema = Yup.object({
  name: Yup.string()
    .max(250, 'Must be less than 250 letters.')
    .when('isCorporate', {
      is: false,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
  address: Yup.string()
    .max(250, 'Must be less than 250 letters.')
    .when('isCorporate', {
      is: false,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
  city: Yup.string()
    .max(250, 'Must be less than 250 letters.')
    .when('isCorporate', {
      is: false,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
  state: Yup.string().when('isCorporate', {
    is: false,
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.nullable(),
  }),
  zipCode: Yup.string().when('isCorporate', {
    is: false,
    then: (schema) => schema.required('Required').matches(zipCodeRegEx, 'Please enter a valid Zip / Postal code'),
    otherwise: (schema) => schema.nullable(),
  }),
  phone: Yup.string().when('isCorporate', {
    is: false,
    then: (schema) => schema.required('Required').matches(phoneRegEx, 'Please enter a valid phone number'),
    otherwise: (schema) => schema.nullable(),
  }),
  email: Yup.string().when('isCorporate', {
    is: false,
    then: (schema) => schema.required('Required').matches(emailRegEx, 'Please enter a valid email address'),
    otherwise: (schema) => schema.nullable(),
  }),

  socialSecurity: Yup.string().when('isCorporate', {
    is: false,
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.nullable(),
  }),
  ownershipPercent: Yup.number()
    .transform((val) => (Number.isNaN(val) ? 0 : val))
    .when('isCorporate', {
      is: false,
      then: (schema) =>
        schema.typeError('Must be a number').required('Required').min(0, 'Should not be negative').max(100, 'Should not exceed 100'),
      otherwise: (schema) => schema.integer().nullable(),
    }),
  isCorporate: Yup.boolean().typeError('').nullable(),
  establishedDate: Yup.string()
    .when('isCorporate', {
      is: true,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    })
    .test('date', 'Please select past date', (val) => {
      if (val) {
        return new Date(val).valueOf() <= Date.now();
      }
      return true;
    }),
  fein: Yup.string().when('isCorporate', {
    is: true,
    then: (schema) => schema.required('Required').min(9, 'Must be 9 digits').max(9, 'Must be 9 digits'),
    otherwise: (schema) => schema.nullable(),
  }),
  website: Yup.string().when('isCorporate', {
    is: true,
    then: (schema) =>
      schema.matches(websiteRegex, {
        excludeEmptyString: true,
        message: 'Please enter a valid website address',
      }),
    otherwise: (schema) => schema.nullable(),
  }),
  leadContact: Yup.string().when('isCorporate', {
    is: true,
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.nullable(),
  }),
  contactPhone: Yup.string().when('isCorporate', {
    is: true,
    then: (schema) => schema.required('Required').matches(phoneRegEx, 'Please enter a valid phone number'),
    otherwise: (schema) => schema.nullable(),
  }),
  contactEmail: Yup.string().when('isCorporate', {
    is: true,
    then: (schema) => schema.required('Required').matches(emailRegEx, 'Please enter a valid email address'),
    otherwise: (schema) => schema.nullable(),
  }),
});

const part1Schema = Yup.object({
  orgInfo: Yup.object({
    businessName: Yup.string().required('Required').max(250, 'Must be less than 250 letters.'),
    businessAddress: Yup.string().required('Required').max(250, 'Must be less than 250 letters.'),
    businessCity: Yup.string().required('Required').max(250, 'Must be less than 250 letters.'),
    state: Yup.string().required('Required'),
    zipCode: Yup.string()
      .typeError('Must be a number')
      .required('Required')
      .matches(zipCodeRegEx, 'Please enter a valid Zip / Postal code'),
    businessPhone: Yup.string().required('Required').matches(phoneRegEx, 'Please enter a valid phone number'),
    website: Yup.string()
      .matches(websiteRegex, {
        excludeEmptyString: true,
        message: 'Please enter a valid website address',
      })
      .max(50, 'Must be less than 50 letters.'),
    establishedDate: Yup.string()
      .required('Required')
      .test('date', 'Please select past date', (val) => {
        if (val) {
          return new Date(val).valueOf() <= Date.now();
        }
        return true;
      }),
    fein: Yup.string().required('Required').min(9, 'Must be 9 digits').max(9, 'Must be 9 digits'),
    leadContact: Yup.string().max(250, 'Must be less than 250 letters.').required('Required'),
    phone: Yup.string().required('Required').matches(phoneRegEx, 'Please enter a valid phone number'),
    email: Yup.string().required('Required').matches(emailRegEx, 'Please enter a valid email address'),
    cclfFinancing: Yup.boolean().nullable(),
    socialDesignation: Yup.string().required('Required'),
    legalStatus: Yup.string().required('Required'),
    sponsors: Yup.array().of(sponsorSchema),
  }),
});

const principalSchema = Yup.object({
  name: Yup.string().max(250, 'Must be less than 250 letters.').required('Required'),
  ownershipPercent: Yup.number()
    .typeError('Must be a number')
    .required('Required')
    .min(0, 'Should not be negative')
    .max(100, 'Should not exceed 100'),
  yearsExperience: Yup.number().typeError('Must be a number').min(0, 'Must be greater than 0'),
});

const part2Schema = Yup.object({
  orgExperience: Yup.object({
    priorProjects: Yup.array().of(
      Yup.object({
        address: Yup.string().max(250, 'Must be less than 250 letters.').required('Required'),
        city: Yup.string().max(250, 'Must be less than 250 letters.').required('Required'),
        state: Yup.string().required('Required'),
        zipCode: Yup.string().required('Required').typeError('Must be a number').matches(zipCodeRegEx, {
          excludeEmptyString: true,
          message: 'Please enter a valid Zip / Postal code',
        }),
        projectType: Yup.string().required('Required'),
        constructionType: Yup.string().required('Required'),
        completionDate: Yup.string()
          .required('Required')
          .test('date', 'Please select past date', (val) => {
            if (val) {
              return new Date(val).valueOf() <= Date.now();
            }
            return true;
          }),
        status: Yup.string().required('Required'),
        projectCost: Yup.number().typeError('Must be a number').required('Required').min(0, 'Must be a positive number or 0'),
      })
    ),
    keyPrincipals: Yup.array().of(principalSchema),
  }),
});

const part3Schema = Yup.object({
  financialRequest: Yup.object({
    loanRequest: Yup.object({
      loanRequestAmount: Yup.number().typeError('Must be a number').required('Required').positive('Must be a positive number'),
      maxUnits: Yup.number().typeError('Must be a number').required('Required').min(0, 'Should not be negative').nullable(),
    }),
    projectInfo: Yup.object({
      address: Yup.string().max(250, 'Must be less than 250 letters.'),
      city: Yup.string().max(250, 'Must be less than 250 letters.'),
      state: Yup.string(),
      zipCode: Yup.string().matches(zipCodeRegEx, {
        excludeEmptyString: true,
        message: 'Please enter a valid Zip / Postal code',
      }),
      sources: Yup.object({
        borrowerEquity: Yup.number()
          .typeError('Must be a number')
          .min(0, 'Must be a positive number or 0')
          .transform((val) => (Number.isNaN(val) ? 0 : val)),
        debt: Yup.number()
          .typeError('Must be a number')
          .min(0, 'Must be a positive number or 0')
          .transform((val) => (Number.isNaN(val) ? 0 : val)),
      }),
      uses: Yup.object({
        averageAcquisition: Yup.number()
          .typeError('Must be a number')
          .min(0, 'Must be a positive number or 0')
          .transform((val) => (Number.isNaN(val) ? 0 : val)),
        averageRehabilitation: Yup.number()
          .typeError('Must be a number')
          .min(0, 'Must be a positive number or 0')
          .transform((val) => (Number.isNaN(val) ? 0 : val)),
        tenPContingency: Yup.number()
          .typeError('Must be a number')
          .min(0, 'Must be a positive number or 0')
          .transform((val) => (Number.isNaN(val) ? 0 : val)),
        softCost: Yup.number()
          .typeError('Must be a number')
          .min(0, 'Must be a positive number or 0')
          .transform((val) => (Number.isNaN(val) ? 0 : val)),
        closingCost: Yup.number()
          .typeError('Must be a number')
          .min(0, 'Must be a positive number or 0')
          .transform((val) => (Number.isNaN(val) ? 0 : val)),
      }).test('is-equal', 'Total Uses is not equal to total Sources', (value, { parent: { sources }, createError }) => {
        const totalUsesSum = Object.values(value || {}).reduce((partialSum = 0, a = 0) => partialSum + a, 0);
        const totalSources = Object.values((sources as any) || {}).reduce<number>(
          (partialSum = 0, a = 0) => partialSum + ((a as number) || 0),
          0
        );
        const isEqual = totalUsesSum === totalSources;
        if (isEqual) {
          return isEqual;
        } else {
          return createError({
            path: 'projectInfoSection',
            message: isEqual ? undefined : 'Uses is not equal to Sources',
          });
        }
      }),
    }),
  }),
});

const authorizedSignerSchema = Yup.object({
  authorizedSigner: Yup.string().required('Required'),
});

const characterReferenceSchema = Yup.object({
  name: Yup.string().required('Required').max(250, 'Must be less than 250 letters.'),
  relationship: Yup.string().required('Required').max(250, 'Must be less than 250 letters.'),
  phone: Yup.string().required('Required').matches(phoneRegEx, {
    excludeEmptyString: true,
    message: 'Please enter a valid phone number',
  }),
  email: Yup.string().required('Required').matches(emailRegEx, {
    excludeEmptyString: true,
    message: 'Please enter a valid email address',
  }),
});
const part4Schema = Yup.object({
  disclosuresReferences: Yup.object({
    disclosure: Yup.object({
      authorizedSigners: Yup.array().of(authorizedSignerSchema).min(1, 'At least one Authorized signature is required.'),
      hasLawsuits: Yup.boolean().nullable(),
      hasLawsuitsExplain: Yup.string().when('hasLawsuits', {
        is: true,
        then: (schema) => schema.required('Required'),
        otherwise: (schema) => schema.nullable(),
      }),
      defaultedOnLoan: Yup.boolean().nullable(),
      defaultLoanExplain: Yup.string().when('defaultedOnLoan', {
        is: true,
        then: (schema) => schema.required('Required'),
        otherwise: (schema) => schema.nullable(),
      }),
    }),
    characterReference: Yup.array().of(characterReferenceSchema),
  }),
});

export const validationSchema = [part1Schema, part2Schema, part3Schema, part4Schema];
